import { apiClient } from './axios';

// ── ConfigPatch types (mirrors backend pipeline.types.ts) ────────────────────

export interface PipelinePromptEdit {
  op: 'replace' | 'insert_after' | 'insert_before' | 'delete';
  find: string;
  replace?: string;
}

export interface TextFieldEdit {
  field: string;
  edits: PipelinePromptEdit[];
  append?: string;
}

export interface ConfigPatch {
  rationale: string;
  textEdits: TextFieldEdit[];
  fieldPatch?: Record<string, unknown>;
}

// ── Candidate diff (computed by backend: main head vs candidate branch head) ─

export interface SnapshotDiffEntry {
  field: string;
  before: unknown;
  after: unknown;
}

export interface CandidateDiff {
  textFields: Array<{ field: string; before: string | null; after: string | null }>;
  otherFields: SnapshotDiffEntry[];
}

// ── Run types ────────────────────────────────────────────────────────────────

export interface PipelineRun {
  id: string;
  configuration_id: string;
  trigger: string;
  window_from: string;
  window_to: string;
  autonomy_level: string;
  status: string;
  stage: string;
  progress: {
    stage: string;
    current: number;
    total: number;
    message: string;
    updatedAt: string;
  } | null;
  summary: Record<string, unknown>;
  error: string | null;
  promoted_commit_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PipelineRunDetail extends PipelineRun {
  findings: Array<{
    id: string;
    call_id: string;
    assessment_name: string;
    verdict: string | null;
    verdict_reasoning: string | null;
    original_flags: Record<string, unknown>;
    root_cause_id: string | null;
  }>;
  root_causes: Array<{
    id: string;
    title: string;
    description: string;
    target_area: string;
    patch: ConfigPatch | null;
  }>;
  test_cases: Array<{
    id: string;
    source_call_id: string;
    kind: string;
    status: string;
    result: string | null;
    test_call_id: string | null;
  }>;
  /** Candidate diff: main head vs candidate branch head, computed by the backend. */
  candidate_diff?: CandidateDiff | null;
}

export interface PipelineRunReport {
  report_html: string | null;
  report_json: Record<string, unknown> | null;
  report_delivered_at: string | null;
  report_error: string | null;
}

export interface ImprovementSettings {
  configuration_id: string;
  autonomy_level: 'off' | 'propose' | 'auto';
  schedule_cron: string;
  timezone: string;
  max_iterations: number;
  held_out_size: number;
  run_baseline_replay: boolean;
  max_test_cases: number | null;
  max_prompt_growth_pct: number;
  allowed_areas: string[];
  report_workflow_id: string | null;
  verifier_model: string;
  patch_model: string;
  simulator_model: string;
  /** AUTO guardrail fields (added by Task 1 migration) */
  min_confirmed_findings?: number;
  promotion_cooldown_hours?: number;
  rollback_window_calls?: number;
  rollback_window_hours?: number;
  rollback_threshold_pct?: number;
}

export const pipelineRunsApi = {
  list: async (params?: { configuration_id?: string; status?: string }) =>
    (await apiClient.get<PipelineRun[]>('/agent-improvement/runs', { params })).data,

  get: async (id: string) =>
    (await apiClient.get<PipelineRunDetail>(`/agent-improvement/runs/${id}`)).data,

  create: async (data: { configurationId: string; callIds?: string[] }) =>
    (
      await apiClient.post<PipelineRun>('/agent-improvement/runs', {
        configurationId: data.configurationId,
        callIds: data.callIds,
      })
    ).data,

  cancel: async (id: string) =>
    (await apiClient.post<PipelineRun>(`/agent-improvement/runs/${id}/cancel`)).data,

  promote: async (id: string) =>
    (await apiClient.post(`/agent-improvement/runs/${id}/promote`)).data,

  discard: async (id: string) =>
    (await apiClient.post(`/agent-improvement/runs/${id}/discard`)).data,

  getReport: async (id: string) =>
    (await apiClient.get<PipelineRunReport>(`/agent-improvement/runs/${id}/report`)).data,

  resendReport: async (id: string) =>
    (await apiClient.post<PipelineRunReport>(`/agent-improvement/runs/${id}/resend-report`)).data,

  getSettings: async (configurationId: string) =>
    (await apiClient.get<ImprovementSettings>(`/agent-improvement/settings/${configurationId}`))
      .data,

  updateSettings: async (configurationId: string, data: Partial<ImprovementSettings>) =>
    (
      await apiClient.put<ImprovementSettings>(
        `/agent-improvement/settings/${configurationId}`,
        data,
      )
    ).data,
};
